// src/components/Header.js
import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Link, useLocation } from 'react-router-dom';
import axiosInstance from '../services/api';
import DynamicProductMenu from './DynamicProductMenu';

const Header = () => {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [productTypes, setProductTypes] = useState([]);
  const [productSubTypes, setProductSubTypes] = useState([]);
  const [openTypeId, setOpenTypeId] = useState(null);
  const [urunlerOpen, setUrunlerOpen] = useState(false);

  useEffect(() => {
    axiosInstance.get('/producttypes').then(r => setProductTypes(r.data)).catch(() => {});
    axiosInstance.get('/productsubtypes').then(r => setProductSubTypes(r.data)).catch(() => {});
  }, []);

  const navBtnSx = (path) => ({
    color: 'white',
    fontWeight: 600,
    fontSize: '0.95rem',
    textTransform: 'none',
    px: 2,
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 6,
      left: '50%',
      transform: 'translateX(-50%)',
      width: location.pathname === path ? '60%' : '0%',
      height: '2px',
      backgroundColor: 'white',
      transition: 'width 0.25s ease',
      borderRadius: '1px',
    },
    '&:hover::after': { width: '60%' },
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
  });

  const drawer = (
    <Box sx={{ width: 280 }} role="presentation">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, backgroundColor: '#f08a0c' }}>
        <img src="/logo.png" alt="İremOto" style={{ height: '36px' }} />
        <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <List disablePadding>
        <ListItemButton component={Link} to="/" onClick={() => setDrawerOpen(false)}>
          <ListItemText primary="Ana Sayfa" primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItemButton>
        <Divider />

        {/* Ürünler (kategoriler) — desktop "Ürünler" menüsüyle aynı yapı */}
        <ListItemButton onClick={() => setUrunlerOpen((o) => !o)}>
          <ListItemText primary="Ürünler" primaryTypographyProps={{ fontWeight: 600 }} />
          {urunlerOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={urunlerOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            <ListItemButton component={Link} to="/urunler" onClick={() => setDrawerOpen(false)} sx={{ pl: 4 }}>
              <ListItemText primary="Tüm Ürünler" primaryTypographyProps={{ fontSize: '0.92rem', fontWeight: 600, color: '#f08a0c' }} />
            </ListItemButton>
            {productTypes.map((pt) => {
              const subs = productSubTypes.filter(s => s.productTypeId === pt.id);
              const isOpen = openTypeId === pt.id;
              return (
                <React.Fragment key={pt.id}>
                  <ListItemButton onClick={() => setOpenTypeId(isOpen ? null : pt.id)} sx={{ pl: 4 }}>
                    <ListItemText primary={pt.name} primaryTypographyProps={{ fontSize: '0.92rem', fontWeight: 600 }} />
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List disablePadding>
                      {subs.length > 0 ? subs.map(st => (
                        <ListItemButton
                          key={st.id}
                          component={Link}
                          to={`/products?subtype=${st.id}`}
                          onClick={() => setDrawerOpen(false)}
                          sx={{ pl: 6 }}
                        >
                          <ListItemText primary={st.name} primaryTypographyProps={{ fontSize: '0.85rem', color: '#f08a0c' }} />
                        </ListItemButton>
                      )) : (
                        <ListItemButton disabled sx={{ pl: 6 }}>
                          <ListItemText primary="Alt kategori yok" primaryTypographyProps={{ fontSize: '0.8rem', color: '#aaa' }} />
                        </ListItemButton>
                      )}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>
        </Collapse>
        <Divider />

        <ListItemButton component={Link} to="/hakkimizda" onClick={() => setDrawerOpen(false)}>
          <ListItemText primary="Hakkımızda" primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItemButton>
        <Divider />
        <ListItemButton component={Link} to="/contact" onClick={() => setDrawerOpen(false)}>
          <ListItemText primary="İletişim" primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: '#f08a0c',
          boxShadow: '0 2px 8px rgba(240,138,12,0.4)',
          zIndex: 1100,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Mobil: hamburger */}
          <IconButton
            sx={{ color: 'white', display: { xs: 'flex', md: 'none' } }}
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon />
          </IconButton>

          {/* Desktop: menü ortada */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mx: 'auto', gap: 0.5 }}>
            <Button sx={navBtnSx('/')} component={Link} to="/">
              Ana Sayfa
            </Button>
            <DynamicProductMenu />
            <Button sx={navBtnSx('/hakkimizda')} component={Link} to="/hakkimizda">
              Hakkımızda
            </Button>
            <Button sx={navBtnSx('/contact')} component={Link} to="/contact">
              İletişim
            </Button>
          </Box>

          {/* Mobil: logo ortada */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
            <Link to="/">
              <img src="/logo.png" alt="İremOto" style={{ height: '36px', display: 'block' }} />
            </Link>
          </Box>

          {/* Sağ boşluk dengesi (mobil) */}
          <Box sx={{ width: 40, display: { xs: 'flex', md: 'none' } }} />
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
